import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import DataEditor, {
  GridCellKind,
  GridColumn,
  Item,
  GridCell,
} from '@glideapps/glide-data-grid';
import {
  CompaniesGridProps,
  GetCompaniesResponse,
  CompanyGridItem,
  GetDncListResponse,
  DncOrganisationItem,
  CrmEvents,
  CrmCustomFieldDefinition,
  GetCustomFieldDefinitionsResponse,
} from '@dreamai/types';
import api from '../../../../api';
import { usePusher } from '../../../../contexts/PusherContext';
import { ReactComponent as DownloadIcon } from '../../../../icons/download.svg';
import CreateCampaignModal from '../../ExpansiveSideNavigation/CreateCampaignModal';
import { createCompanyCanvasAsset } from './companyCanvasHelper';
import '@glideapps/glide-data-grid/dist/index.css';
import './crm-grid.scss';

interface CompaniesGridComponentProps extends CompaniesGridProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

type TabType = 'all' | 'dnc';

const CompaniesGrid: React.FC<CompaniesGridComponentProps> = ({
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [companies, setCompanies] = useState<CompanyGridItem[]>([]);
  const [dncOrganisations, setDncOrganisations] = useState<
    DncOrganisationItem[]
  >([]);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [loading, setLoading] = useState(true);
  const [dncLoading, setDncLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [sourceFilterOpen, setSourceFilterOpen] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    colIndex: number;
    direction: 'asc' | 'desc';
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<
    CrmCustomFieldDefinition[]
  >([]);
  const [customFieldFilters, setCustomFieldFilters] = useState<
    Record<string, string[]>
  >({});
  const [activeCustomFieldFilter, setActiveCustomFieldFilter] = useState<
    string | null
  >(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [loading]);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetCompaniesResponse>(
        '/crm-data/companies',
      );

      if (response.data.success) {
        setCompanies(response.data.companies);
      } else {
        setError(response.data.error || 'Failed to load companies');
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchDncOrganisations = useCallback(async () => {
    try {
      setDncLoading(true);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetDncListResponse>(
        '/crm-data/dnc?type=organisations',
      );

      if (response.data.success && response.data.organisations) {
        setDncOrganisations(response.data.organisations);
      }
    } catch (err) {
      console.error('Error fetching DNC organisations:', err);
    } finally {
      setDncLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchCustomFieldDefinitions = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetCustomFieldDefinitionsResponse>(
        '/crm-data/custom-fields',
      );

      if (response.data.success) {
        setCustomFieldDefinitions(
          response.data.fields.filter((f) => f.visibleInGrid),
        );
      }
    } catch (err) {
      console.error('Error fetching custom field definitions:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchCompanies();
    fetchDncOrganisations();
    fetchCustomFieldDefinitions();
  }, [fetchCompanies, fetchDncOrganisations, fetchCustomFieldDefinitions]);

  // Listen for organisation created/updated events to refresh the grid
  useEffect(() => {
    if (!pusherChannel) return;

    const handleOrganisationChange = () => {
      fetchCompanies();
    };

    pusherChannel.bind(
      CrmEvents.ORGANISATION_CREATED,
      handleOrganisationChange,
    );
    pusherChannel.bind(
      CrmEvents.ORGANISATION_UPDATED,
      handleOrganisationChange,
    );

    // Listen for custom field definition updates
    const handleCustomFieldUpdate = () => {
      fetchCustomFieldDefinitions();
      fetchCompanies(); // Also refresh companies to get updated custom field values
    };

    pusherChannel.bind(
      CrmEvents.CUSTOM_FIELD_DEFINITION_UPDATED,
      handleCustomFieldUpdate,
    );

    return () => {
      pusherChannel.unbind(
        CrmEvents.ORGANISATION_CREATED,
        handleOrganisationChange,
      );
      pusherChannel.unbind(
        CrmEvents.ORGANISATION_UPDATED,
        handleOrganisationChange,
      );
      pusherChannel.unbind(
        CrmEvents.CUSTOM_FIELD_DEFINITION_UPDATED,
        handleCustomFieldUpdate,
      );
    };
  }, [pusherChannel, fetchCompanies, fetchCustomFieldDefinitions]);

  // Status options for the filter (excluding 'all' - empty array means all)
  const statusOptions = useMemo(
    () => [
      { value: 'none', label: 'None' },
      { value: 'opportunity', label: 'Opportunity' },
      { value: 'trial', label: 'Trial' },
      { value: 'current', label: 'Current Customer' },
      { value: 'past', label: 'Past Customer' },
      { value: 'churned', label: 'Churned' },
    ],
    [],
  );

  // Base column count before custom fields
  const BASE_COLUMN_COUNT = 7;

  // Helper to format date for display
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const filteredCompanies = useMemo(() => {
    let result = companies;

    // Apply status filter (empty array = all statuses)
    if (statusFilter.length > 0) {
      result = result.filter((c) =>
        statusFilter.includes(c.customerStatus || 'opportunity'),
      );
    }

    // Apply source filter
    if (sourceFilter.length > 0) {
      result = result.filter((c) => sourceFilter.includes(c.source || ''));
    }

    // Apply custom field filters
    Object.entries(customFieldFilters).forEach(([fieldId, filterValues]) => {
      if (filterValues.length > 0) {
        const fieldDef = customFieldDefinitions.find((f) => f.id === fieldId);
        if (fieldDef) {
          result = result.filter((c) => {
            const fieldValue = c.customFields?.find(
              (cf) => cf.fieldName === fieldDef.fieldName,
            );
            return filterValues.includes(fieldValue?.value || '');
          });
        }
      }
    });

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.domain?.toLowerCase().includes(query) ||
          c.linkedinUrl?.toLowerCase().includes(query) ||
          c.instagramHandle?.toLowerCase().includes(query),
      );
    }

    // Apply sorting
    if (sortConfig) {
      const { colIndex, direction } = sortConfig;
      result = [...result].sort((a, b) => {
        let aVal = '';
        let bVal = '';
        switch (colIndex) {
          case 0:
            aVal = a.name;
            bVal = b.name;
            break;
          case 1:
            aVal = a.customerStatus || 'opportunity';
            bVal = b.customerStatus || 'opportunity';
            break;
          case 2:
            aVal = a.linkedinUrl || '';
            bVal = b.linkedinUrl || '';
            break;
          case 3:
            aVal = a.domain || '';
            bVal = b.domain || '';
            break;
          case 4:
            aVal = a.instagramHandle || '';
            bVal = b.instagramHandle || '';
            break;
          case 5:
            aVal = a.source || '';
            bVal = b.source || '';
            break;
          case 6:
            aVal = a.createdAt || '';
            bVal = b.createdAt || '';
            break;
          default: {
            // Handle custom field columns
            if (colIndex >= BASE_COLUMN_COUNT) {
              const fieldIndex = colIndex - BASE_COLUMN_COUNT;
              const fieldDef = customFieldDefinitions[fieldIndex];
              if (fieldDef) {
                const aField = a.customFields?.find(
                  (cf) => cf.fieldName === fieldDef.fieldName,
                );
                const bField = b.customFields?.find(
                  (cf) => cf.fieldName === fieldDef.fieldName,
                );
                aVal = aField?.value || '';
                bVal = bField?.value || '';
              }
            }
            break;
          }
        }
        const aStr = (aVal || '').toLowerCase();
        const bStr = (bVal || '').toLowerCase();
        if (aStr < bStr) return direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [
    companies,
    searchQuery,
    sortConfig,
    statusFilter,
    customFieldFilters,
    customFieldDefinitions,
  ]);

  const filteredDncOrganisations = useMemo(() => {
    let result = dncOrganisations;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (d) =>
          d.organisationName.toLowerCase().includes(query) ||
          d.campaignName.toLowerCase().includes(query) ||
          d.reason?.toLowerCase().includes(query),
      );
    }

    return result;
  }, [dncOrganisations, searchQuery]);

  const columns: GridColumn[] = useMemo(() => {
    const baseColumns: GridColumn[] = [
      {
        title: `Name${sortConfig?.colIndex === 0 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'name',
        width: columnWidths['name'] ?? 250,
      },
      {
        title: `Status${sortConfig?.colIndex === 1 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}${statusFilter.length > 0 ? ` (${statusFilter.length})` : ''}`,
        id: 'customerStatus',
        width: columnWidths['customerStatus'] ?? 160,
        hasMenu: true,
      },
      {
        title: `LinkedIn URL${sortConfig?.colIndex === 2 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'linkedinUrl',
        width: columnWidths['linkedinUrl'] ?? 300,
      },
      {
        title: `Domain${sortConfig?.colIndex === 3 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'domain',
        width: columnWidths['domain'] ?? 200,
      },
      {
        title: `Instagram${sortConfig?.colIndex === 4 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'instagramHandle',
        width: columnWidths['instagramHandle'] ?? 180,
      },
      {
        title: `Source${sortConfig?.colIndex === 5 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}${sourceFilter.length > 0 ? ` (${sourceFilter.length})` : ''}`,
        id: 'source',
        width: columnWidths['source'] ?? 180,
        hasMenu: true,
      },
      {
        title: `Added At${sortConfig?.colIndex === 6 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'createdAt',
        width: columnWidths['createdAt'] ?? 120,
      },
    ];

    // Add custom field columns
    const customColumns: GridColumn[] = customFieldDefinitions.map(
      (field, idx) => {
        const colIndex = BASE_COLUMN_COUNT + idx;
        const filterCount = customFieldFilters[field.id]?.length || 0;
        const colId = `customField_${field.id}`;
        return {
          title: `${field.fieldName}${sortConfig?.colIndex === colIndex ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}${filterCount > 0 ? ` (${filterCount})` : ''}`,
          id: colId,
          width: columnWidths[colId] ?? 150,
          hasMenu: field.fieldType === 'enum' || field.fieldType === 'boolean',
        };
      },
    );

    return [...baseColumns, ...customColumns];
  }, [
    sortConfig,
    statusFilter,
    customFieldDefinitions,
    customFieldFilters,
    columnWidths,
  ]);

  const onColumnResize = useCallback((column: GridColumn, newSize: number) => {
    if (column.id) {
      setColumnWidths((prev) => ({ ...prev, [column.id as string]: newSize }));
    }
  }, []);

  const dncColumns: GridColumn[] = useMemo(
    () => [
      { title: 'Organisation Name', id: 'organisationName', width: 250 },
      { title: 'Campaign', id: 'campaignName', width: 200 },
      { title: 'Reason', id: 'reason', width: 300 },
      { title: 'Added', id: 'createdAt', width: 150 },
    ],
    [],
  );

  const onHeaderClicked = useCallback((colIndex: number) => {
    setSortConfig((prev) => {
      if (prev?.colIndex === colIndex) {
        // Toggle direction or clear
        if (prev.direction === 'asc') {
          return { colIndex, direction: 'desc' };
        }
        return null; // Clear sort
      }
      return { colIndex, direction: 'asc' };
    });
  }, []);

  const onHeaderMenuClick = useCallback(
    (
      colIndex: number,
      bounds: { x: number; y: number; width: number; height: number },
    ) => {
      // Position dropdown at right edge of column header, below it
      setFilterDropdownPosition({
        x: bounds.x + bounds.width - 180,
        y: bounds.y + bounds.height + 4,
      });

      // Status column (index 1)
      if (colIndex === 1) {
        setActiveCustomFieldFilter(null);
        setSourceFilterOpen(false);
        setStatusFilterOpen(true);
        return;
      }

      // Source column (index 5)
      if (colIndex === 5) {
        setActiveCustomFieldFilter(null);
        setStatusFilterOpen(false);
        setSourceFilterOpen(true);
        return;
      }

      // Custom field enum/boolean columns (col >= BASE_COLUMN_COUNT)
      if (colIndex >= BASE_COLUMN_COUNT) {
        const fieldIndex = colIndex - BASE_COLUMN_COUNT;
        const fieldDef = customFieldDefinitions[fieldIndex];
        if (
          fieldDef?.fieldType === 'enum' ||
          fieldDef?.fieldType === 'boolean'
        ) {
          setStatusFilterOpen(false);
          setActiveCustomFieldFilter(fieldDef.id);
        }
      }
    },
    [customFieldDefinitions],
  );

  // Close filter dropdown when clicking outside
  useEffect(() => {
    if (!statusFilterOpen && !sourceFilterOpen && !activeCustomFieldFilter)
      return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.column-filter-dropdown')) {
        setStatusFilterOpen(false);
        setSourceFilterOpen(false);
        setActiveCustomFieldFilter(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusFilterOpen, sourceFilterOpen, activeCustomFieldFilter]);

  // Get unique source values from companies for filtering
  const uniqueSourceValues = useMemo(() => {
    const sources = new Set<string>();
    companies.forEach((c) => {
      if (c.source) sources.add(c.source);
    });
    return Array.from(sources).sort();
  }, [companies]);

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const company = filteredCompanies[row];
      if (!company) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: false,
        };
      }

      switch (col) {
        case 0:
          return {
            kind: GridCellKind.Text,
            data: company.name,
            displayData: company.name,
            allowOverlay: false,
          };
        case 1: {
          const statusLabels: Record<string, string> = {
            none: 'None',
            opportunity: 'Opportunity',
            trial: 'Trial',
            current: 'Current',
            past: 'Past',
            churned: 'Churned',
          };
          const status = company.customerStatus || 'opportunity';
          return {
            kind: GridCellKind.Text,
            data: status,
            displayData: statusLabels[status] || status,
            allowOverlay: false,
          };
        }
        case 2:
          return {
            kind: GridCellKind.Uri,
            data: company.linkedinUrl || '',
            displayData: company.linkedinUrl || '',
            allowOverlay: false,
          };
        case 3:
          return {
            kind: GridCellKind.Uri,
            data: company.domain ? `https://${company.domain}` : '',
            displayData: company.domain || '',
            allowOverlay: false,
          };
        case 4:
          return {
            kind: GridCellKind.Text,
            data: company.instagramHandle || '',
            displayData: company.instagramHandle
              ? `@${company.instagramHandle}`
              : '',
            allowOverlay: false,
          };
        case 5:
          return {
            kind: GridCellKind.Text,
            data: company.source || '',
            displayData: company.source || '',
            allowOverlay: false,
          };
        case 6:
          return {
            kind: GridCellKind.Text,
            data: company.createdAt || '',
            displayData: formatDate(company.createdAt || ''),
            allowOverlay: false,
          };
        default: {
          // Handle custom field columns (col >= BASE_COLUMN_COUNT)
          if (col >= BASE_COLUMN_COUNT) {
            const fieldIndex = col - BASE_COLUMN_COUNT;
            const fieldDef = customFieldDefinitions[fieldIndex];
            if (fieldDef && company.customFields) {
              const fieldValue = company.customFields.find(
                (cf) => cf.fieldName === fieldDef.fieldName,
              );
              const value = fieldValue?.value || '';

              // Show boolean fields as checkboxes
              if (fieldDef.fieldType === 'boolean') {
                return {
                  kind: GridCellKind.Boolean,
                  data: value === 'true',
                  allowOverlay: false,
                };
              }

              return {
                kind: GridCellKind.Text,
                data: value,
                displayData: value,
                allowOverlay: false,
              };
            }
          }
          return {
            kind: GridCellKind.Text,
            data: '',
            displayData: '',
            allowOverlay: false,
          };
        }
      }
    },
    [filteredCompanies, customFieldDefinitions],
  );

  const getDncCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const dnc = filteredDncOrganisations[row];
      if (!dnc) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: false,
        };
      }

      switch (col) {
        case 0:
          return {
            kind: GridCellKind.Text,
            data: dnc.organisationName,
            displayData: dnc.organisationName,
            allowOverlay: false,
          };
        case 1:
          return {
            kind: GridCellKind.Text,
            data: dnc.campaignName,
            displayData: dnc.campaignName,
            allowOverlay: false,
          };
        case 2:
          return {
            kind: GridCellKind.Text,
            data: dnc.reason || '',
            displayData: dnc.reason || '',
            allowOverlay: false,
          };
        case 3:
          return {
            kind: GridCellKind.Text,
            data: dnc.createdAt,
            displayData: dnc.createdAt
              ? new Date(dnc.createdAt).toLocaleDateString()
              : '',
            allowOverlay: false,
          };
        default:
          return {
            kind: GridCellKind.Text,
            data: '',
            displayData: '',
            allowOverlay: false,
          };
      }
    },
    [filteredDncOrganisations],
  );

  const handleExport = useCallback(() => {
    let headers: string[];
    let rows: string[][];
    let filename: string;

    if (activeTab === 'dnc') {
      headers = ['Organisation Name', 'Campaign', 'Reason', 'Added'];
      rows = filteredDncOrganisations.map((d) => [
        d.organisationName,
        d.campaignName,
        d.reason || '',
        d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '',
      ]);
      filename = 'dnc-companies.csv';
    } else {
      headers = ['Name', 'Status', 'LinkedIn URL', 'Domain', 'Instagram'];
      rows = filteredCompanies.map((c) => [
        c.name,
        c.customerStatus || 'opportunity',
        c.linkedinUrl || '',
        c.domain || '',
        c.instagramHandle || '',
      ]);
      filename = 'companies.csv';
    }

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [activeTab, filteredCompanies, filteredDncOrganisations]);

  if (loading) {
    return (
      <div className='crm-grid'>
        <div className='grid-loading-skeleton'>
          <div className='skeleton-header'>
            <div className='skeleton-header-cell skeleton-header-cell--narrow' />
            <div className='skeleton-header-cell skeleton-header-cell--first' />
            <div className='skeleton-header-cell skeleton-header-cell--second' />
          </div>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className='skeleton-row'>
              <div className='skeleton-cell skeleton-cell--narrow' />
              <div className='skeleton-cell'>
                <div className='skeleton-circle' />
              </div>
              <div className='skeleton-cell' />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='crm-grid'>
        <div className='error'>{error}</div>
      </div>
    );
  }

  // Show empty state without toolbar when no data at all from backend (after loading complete)
  if (!loading && companies.length === 0 && dncOrganisations.length === 0) {
    return (
      <div className='empty-state-wrapper-for-grid'>
        <div className='grid-empty-state'>
          <img
            src='/campaigns/nothing-in-grid.png'
            alt='No companies'
            className='empty-state-image'
          />
          <h3 className='empty-state-title'>
            I'll add companies here as we find them
          </h3>
          <p className='empty-state-description'>
            Once your campaigns start prospecting you'll find a list of all
            companies here.
          </p>
          <button
            className='empty-state-button'
            onClick={() => setShowCampaignModal(true)}
          >
            Create a Campaign
          </button>
          <CreateCampaignModal
            show={showCampaignModal}
            onClose={() => setShowCampaignModal(false)}
          />
        </div>
      </div>
    );
  }

  const renderGrid = () => {
    if (activeTab === 'all') {
      // Always show the grid when dimensions are available (even with 0 rows)
      // so user can access column header menus to change filters
      if (dimensions.width > 0 && dimensions.height > 0) {
        return (
          <DataEditor
            columns={columns}
            rows={filteredCompanies.length}
            getCellContent={getCellContent}
            getCellsForSelection={true}
            width={dimensions.width}
            height={dimensions.height}
            rowMarkers='number'
            smoothScrollX
            smoothScrollY
            onHeaderClicked={(colIndex) => onHeaderClicked(colIndex)}
            onHeaderMenuClick={(colIndex, bounds) =>
              onHeaderMenuClick(colIndex, bounds)
            }
            onColumnResize={onColumnResize}
            onCellClicked={(cell) => {
              const [col, row] = cell;
              if (col !== 0) return;
              const company = filteredCompanies[row];
              if (company && threadId) {
                createCompanyCanvasAsset({
                  companyId: company.id,
                  companyName: company.name,
                  threadId,
                  getAccessTokenSilently,
                });
              }
            }}
          />
        );
      }
      return null; // Waiting for dimensions
    }

    // DNC tab
    if (dncLoading) {
      return <div className='grid-loading'>Loading...</div>;
    }

    if (
      dimensions.width > 0 &&
      dimensions.height > 0 &&
      filteredDncOrganisations.length > 0
    ) {
      return (
        <DataEditor
          columns={dncColumns}
          rows={filteredDncOrganisations.length}
          getCellContent={getDncCellContent}
          getCellsForSelection={true}
          width={dimensions.width}
          height={dimensions.height}
          rowMarkers='number'
          smoothScrollX
          smoothScrollY
        />
      );
    }

    return (
      <div className='empty-state-wrapper-for-grid'>
        <div className='grid-empty-state'>
          <img
            src='/campaigns/nothing-in-grid.png'
            alt='No DNC entries'
            className='empty-state-image'
          />
          <h3 className='empty-state-title'>
            No organisations on the Do Not Contact list
          </h3>
          <p className='empty-state-description'>
            Organisations added to DNC will appear here.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className='crm-grid'>
      {dncOrganisations.length > 0 && (
        <div className='grid-tabs'>
          <button
            className={`grid-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All companies <span className='tab-count'>{companies.length}</span>
          </button>
          <button
            className={`grid-tab ${activeTab === 'dnc' ? 'active' : ''}`}
            onClick={() => setActiveTab('dnc')}
          >
            Do not contact{' '}
            <span className='tab-count'>{dncOrganisations.length}</span>
          </button>
        </div>
      )}

      <div className='toolbar'>
        <div className='search'>
          <span className='search-label'>
            Search {activeTab === 'all' ? 'Companies' : 'DNC List'}
          </span>
          <input
            type='text'
            className='search-input'
            placeholder=''
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className='actions'>
          <button className='export-btn' onClick={handleExport}>
            <DownloadIcon />
          </button>
        </div>
      </div>

      <div
        className='grid-container'
        ref={containerRef}
        style={{
          width: '100%',
          flex: '1 1 0',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {renderGrid()}
      </div>

      {/* Status filter dropdown */}
      {statusFilterOpen && filterDropdownPosition && (
        <div
          className='column-filter-dropdown'
          style={{
            position: 'fixed',
            left: filterDropdownPosition.x,
            top: filterDropdownPosition.y,
            zIndex: 1000,
          }}
        >
          <div className='filter-dropdown-header'>
            Filter by Status
            {statusFilter.length > 0 && (
              <button
                className='filter-clear-btn'
                onClick={() => setStatusFilter([])}
              >
                Clear
              </button>
            )}
          </div>
          {statusOptions.map((option) => {
            const isSelected = statusFilter.includes(option.value);
            return (
              <button
                key={option.value}
                className={`filter-dropdown-option ${isSelected ? 'active' : ''}`}
                onClick={() => {
                  setStatusFilter((prev) =>
                    isSelected
                      ? prev.filter((v) => v !== option.value)
                      : [...prev, option.value],
                  );
                }}
              >
                <span className='filter-checkbox'>{isSelected ? '✓' : ''}</span>
                {option.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Source filter dropdown */}
      {sourceFilterOpen && filterDropdownPosition && (
        <div
          className='column-filter-dropdown'
          style={{
            position: 'fixed',
            left: filterDropdownPosition.x,
            top: filterDropdownPosition.y,
            zIndex: 1000,
          }}
        >
          <div className='filter-dropdown-header'>
            Filter by Source
            {sourceFilter.length > 0 && (
              <button
                className='filter-clear-btn'
                onClick={() => setSourceFilter([])}
              >
                Clear
              </button>
            )}
          </div>
          {uniqueSourceValues.map((value) => {
            const isSelected = sourceFilter.includes(value);
            return (
              <button
                key={value}
                className={`filter-dropdown-option ${isSelected ? 'active' : ''}`}
                onClick={() => {
                  setSourceFilter((prev) =>
                    isSelected
                      ? prev.filter((v) => v !== value)
                      : [...prev, value],
                  );
                }}
              >
                <span className='filter-checkbox'>{isSelected ? '✓' : ''}</span>
                {value}
              </button>
            );
          })}
        </div>
      )}

      {/* Custom field enum/boolean filter dropdown */}
      {activeCustomFieldFilter &&
        filterDropdownPosition &&
        (() => {
          const fieldDef = customFieldDefinitions.find(
            (f) => f.id === activeCustomFieldFilter,
          );
          if (!fieldDef) return null;

          // Get filter options based on field type
          const filterOptions =
            fieldDef.fieldType === 'boolean'
              ? ['true', 'false']
              : fieldDef.enumValues || [];

          if (filterOptions.length === 0) return null;

          const selectedValues = customFieldFilters[fieldDef.id] || [];
          return (
            <div
              className='column-filter-dropdown'
              style={{
                position: 'fixed',
                left: filterDropdownPosition.x,
                top: filterDropdownPosition.y,
                zIndex: 1000,
              }}
            >
              <div className='filter-dropdown-header'>
                Filter by {fieldDef.fieldName}
                {selectedValues.length > 0 && (
                  <button
                    className='filter-clear-btn'
                    onClick={() =>
                      setCustomFieldFilters((prev) => ({
                        ...prev,
                        [fieldDef.id]: [],
                      }))
                    }
                  >
                    Clear
                  </button>
                )}
              </div>
              {filterOptions.map((value) => {
                const isSelected = selectedValues.includes(value);
                const displayLabel =
                  fieldDef.fieldType === 'boolean'
                    ? value === 'true'
                      ? 'Yes'
                      : 'No'
                    : value;
                return (
                  <button
                    key={value}
                    className={`filter-dropdown-option ${isSelected ? 'active' : ''}`}
                    onClick={() => {
                      setCustomFieldFilters((prev) => ({
                        ...prev,
                        [fieldDef.id]: isSelected
                          ? (prev[fieldDef.id] || []).filter((v) => v !== value)
                          : [...(prev[fieldDef.id] || []), value],
                      }));
                    }}
                  >
                    <span className='filter-checkbox'>
                      {isSelected ? '✓' : ''}
                    </span>
                    {displayLabel}
                  </button>
                );
              })}
            </div>
          );
        })()}
    </div>
  );
};

export default CompaniesGrid;
